import React, { useMemo } from 'react';
import type { NavBarProps } from '../../../NavBar';
import { NavBar } from '../../../NavBar';
import type { EventInfoFragmentFragment } from '@graphql/fragments/event.info.graphql.generated';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import type { EventPageType } from '@graphql/generated-base';
import { Category } from '@graphql/generated-base';
import { StyledRoot, StyledTitle, styles } from './EventNameNav.styles';
import { ThemeSprites } from '@shared/utils/styleApplicator/ThemeSprites';
import type { ApplicationContext } from '@shared/utils/styleApplicator/styleApplicator.types';
import { BRANNAN_TITLE_BANNER_MEDIA, useTitleBannerCanvas } from '../../../../LayoutBrannan.theme';
import { Box, ButtonV2, Flex, TextV2 } from '@withjoy/joykit';
import { Link } from '@react-router';
import { GuestLanguageSelector } from '@apps/guest/components/GuestLanguageSelector';
import { useEventUserRole } from '@shared/components/AuthProvider';
import { useTranslation } from '@shared/core/i18n';
import { isInIframe } from '@shared/utils/isInIframe';
import type { NavbarEventPropsInterface } from '@apps/guest/packages/layout-engine/layouts/layout.types';
import { animationTransition } from '@shared/utils/animationTransition';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { InlineEditor } from '@apps/guest/shared/components/InlineEditor';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { Edit } from '@withjoy/joykit/icons';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { useColorPaletteProvider } from '@apps/guest/packages/layout-engine/layouts/ColorPaletteProvider/ColorPaletteProvider';
import { getButtonPropsBasedOnBackground } from '@apps/guest/packages/layout-engine/layouts/layout.utils';
import { CustomArtMobile } from '@apps/guest/packages/layout-engine/components/CustomArtMobile/CustomArtMobile';
import { getMastheadContainerCss, getMastheadLayerCss, getMastheadTitleFloorCss, MASTHEAD_TITLE_PADDING_Y } from '../Masthead/masthead.styles';
import type { ResolvedMasthead } from '@shared/types/Masthead.types';
import type { WebsiteHeaderEventTitle } from '@shared/types/WebsiteHeaderSettings.types';
import { EVENT_TITLE_TYPOGRAPHY_OVERRIDE, getEventNameMinHeight } from '@apps/guest/components/EventNameBanner/eventNameBanner.constants';
import { useEventNameTitleVariant } from '@apps/guest/components/EventNameBanner/useEventNameTitleVariant';
import { useEventDesignThemingProvider } from '@shared/core/website/theming';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { resolveSafeEventTitleHtml } from '@shared/utils/eventTitleHtml';
import { useFontReadyTitleHtml } from './useFontReadyTitleHtml';
import { useReapplyDesignOnMount } from './useReapplyDesignOnMount';
import type { WritableUtmParameters } from '@shared/utils/utmParameters';
import { EVENT_DISPLAY_NAME_MAX_LENGTH } from '@shared/constants/textLimits';

const TITLE_BANNER_ART_CONTEXT: ApplicationContext = { component: 'title-banner' };
// Same split as BRANNAN_TITLE_BANNER_MEDIA: the mobile art shows below sm4 only.
const CUSTOM_ART_MOBILE_DISPLAY = { _: 'block', sm4: 'none' } as const;

export interface EventNameNavProps {
  eventHandle: string;
  info: EventInfoFragmentFragment;
  pages: ReadonlyArray<EventPageFragment>;
  navBarVariant: NavBarProps['variant'];
  applicatorProp: object;
  handleMenuClicked?: (item: string, utm?: WritableUtmParameters) => void;
  handleEditEventClicked?: () => void;
  eventProps: NavbarEventPropsInterface;
  page: Maybe<EventPageType>;
  pageSlug?: string;
  masthead?: ResolvedMasthead | null;
}

interface Props extends EventNameNavProps {
  eventDisplayName: Maybe<string>;
  headerTextData?: WebsiteHeaderEventTitle;
}

/**
 * This is a straight clone of the Header.tsx component
 */
const EventNameNav: React.FC<Props> = ({
  applicatorProp,
  eventHandle,
  info,
  eventDisplayName,
  headerTextData,
  pages,
  navBarVariant,
  handleMenuClicked,
  handleEditEventClicked,
  eventProps,
  page,
  pageSlug,
  masthead,
  children,
  ...restProps
}) => {
  const { isAdmin } = useEventUserRole();
  const [isMultilineTitleEnabled] = useIsFeatureEnabled('websiteHeaderMultilineTitleEnabled');
  // Prefer the header's own rich title (contentHtml — sanitized at save time, per-line font
  // spans) whenever it has content; otherwise (flag off or no title set) fall back to the event
  // display name. The resolver carries the render-time trust boundary: header dataJSON is
  // schemaless, so anything the title editor couldn't have produced falls back instead of being
  // injected into the h1. Shared with the admin template thumbnail so the tile can't drift.
  const titleHtml = resolveSafeEventTitleHtml(headerTextData, isMultilineTitleEnabled);
  const hasMultilineHeaderTitle = !!titleHtml;
  const { hasFont, loadedFonts } = useEventDesignThemingProvider();
  const renderedTitleHtml = useFontReadyTitleHtml(titleHtml, hasFont, loadedFonts);
  // Per-breakpoint title colors (sm4 split mirrors StyledTitle). 'inherit' on an unset side
  // resolves through the existing parents — artBackgroundFontColor on mobile, pageFontColor
  // on desktop — so partial settings keep today's behavior on the other breakpoint.
  const multilineTitleColor =
    hasMultilineHeaderTitle && (headerTextData?.color || headerTextData?.mobileColor)
      ? { _: headerTextData?.mobileColor ?? 'inherit', sm4: headerTextData?.color ?? 'inherit' }
      : undefined;
  const isMobile = useMediaQuery(theme => theme.mediaQueries.between(0, { breakpointAlias: 'sm2' }));
  const eventNameTitleVariant = useEventNameTitleVariant();
  const isMobileOrTablet = useIsMobileOrTablet();
  const { pageColor, pageFontColor, artBackgroundFontColor } = useColorPaletteProvider();

  const { t2 } = useTranslation('eventMenu');
  const editEventText = t2('editEvent');
  const inlineEditorTransFn = useTranslation('joykit').t('inlineEditor');
  const customEditEventButtonProps = useMemo(() => (pageColor ? { ...getButtonPropsBasedOnBackground(pageColor), startIcon: <Edit size={16} /> } : {}), [pageColor]);
  const pageFontColorProps = useMemo(() => (pageFontColor ? { color: pageFontColor } : {}), [pageFontColor]);
  const mastheadContainerCss = masthead ? getMastheadContainerCss(masthead) : {};
  // In guest view InlineEditor renders bare children, so the static h1 needs its own
  // stacking above the positioned art layer; paddingY keeps the name off the frame art.
  // The floor css only lands on top_center art, whose ornaments a one-line name would crop.
  const mastheadTitleProps = masthead
    ? ({
        position: 'relative',
        zIndex: 1,
        paddingY: MASTHEAD_TITLE_PADDING_Y,
        ...getMastheadTitleFloorCss(masthead),
      } as const)
    : {};

  const hasMasthead = !!masthead;

  const isPreviewing = isInIframe();
  const eventNameMinHeight = useMemo(() => getEventNameMinHeight(isPreviewing, hasMasthead), [isPreviewing, hasMasthead]);

  useReapplyDesignOnMount();

  const handleEditTextClick = useEventCallback(() => {
    // The H1 shows the header's own multiline eventName — route the pencil to its editor.
    // editText would edit the event-level display name, a different value.
    if (hasMultilineHeaderTitle) {
      sendMessageToParentWindow({
        action: 'inlineEditingInteraction',
        source: 'joyPreview',
        value: {
          action: 'editHeaderEventTitle',
          inlineEditData: { textContent: titleHtml || '', label: inlineEditorTransFn.displayName() },
        },
      });
      return;
    }
    // send the message to the parent window to open the text editor
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: {
        action: 'editText',
        inlineEditData: { textContent: eventDisplayName || '', label: inlineEditorTransFn.displayName(), validationConstraints: { maxLength: EVENT_DISPLAY_NAME_MAX_LENGTH } },
      },
    });
  });

  const handleEditFontClick = useEventCallback((category: Category) => {
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editFont', inlineEditData: { category } },
    });
  });

  // PRNT-4898 bundled path: small-screen title-banner theme art rendered as React.
  // Empty/inert unless the websiteBundleStyleApplicator flag is affirmatively 'on'.
  const titleBannerCanvas = useTitleBannerCanvas();

  return (
    <>
      <StyledRoot
        {...restProps}
        position={'relative'}
        {...applicatorProp}
        data-uh="nah"
        {...(titleBannerCanvas.containerStyle?.backgroundColor ? { backgroundColor: titleBannerCanvas.containerStyle.backgroundColor } : {})}
        {...(titleBannerCanvas.containerStyle?.color ? { color: titleBannerCanvas.containerStyle.color } : {})}
      >
        {/* First children — the DOM position the legacy applicator's insertBefore produced. */}
        {titleBannerCanvas.sprites.length > 0 && (
          <ThemeSprites
            sprites={titleBannerCanvas.sprites}
            context={TITLE_BANNER_ART_CONTEXT}
            media={BRANNAN_TITLE_BANNER_MEDIA}
          />
        )}
        {/* Hide in CSS, not via isSmallScreen: SSR guesses it from the UA, and a mismatch on hydration
            leaves the art's absolute classes on the title wrappers (iPad landscape overlap, PRNT-5433). */}
        {eventProps.isCustomArtEnabled && (
          <CustomArtMobile
            backgroundImageUrl={eventProps.customArtUrl}
            display={CUSTOM_ART_MOBILE_DISPLAY}
          />
        )}
        <Flex
          width="100%"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight={eventNameMinHeight}
        >
          <Box
            height={{ _: 'auto', sm2: 'auto' }}
            transition={animationTransition('height')}
            width="100%"
            {...pageFontColorProps}
          >
            {/*
              When `matchDesktopPageColorForMobileTitleBanner` is on, the mobile banner is painted with `pageColor`
              and the couple can re-pick "Color on top of art" in the designer to regain contrast — that color isn't
              used elsewhere in Brannan.
            */}
            <StyledTitle
              __css={{ ...styles.title, ...mastheadContainerCss }}
              data-testid="header-title"
              color={{ _: artBackgroundFontColor, sm4: 'inherit' }}
            >
              {masthead && (
                // Full-viewport art layer anchored to the title block — breaks out of the
                // 1024px Brannan card the same way PageOrScreenWidthImage does.
                // pointer-events off so InlineEditor stays usable.
                <Box
                  position="absolute"
                  top={0}
                  bottom={0}
                  width="100vw"
                  left="50%"
                  transform="translateX(-50%)"
                  pointerEvents="none"
                  {...getMastheadLayerCss(masthead)}
                />
              )}
              <InlineEditor
                elementLabel={inlineEditorTransFn.displayName()}
                labelPosition="bottom"
                actionData={{
                  edit: handleEditTextClick,
                  font: () => handleEditFontClick(Category.EVENT_TITLE),
                }}
                wrapperCSS={{
                  zIndex: 'banner',
                }}
                inset={isMobile ? -12 : -16}
                componentName="eventDisplayName"
                pageName={page}
                pageSlug={pageSlug || ''}
              >
                <GuestSiteTypographyOverride override={EVENT_TITLE_TYPOGRAPHY_OVERRIDE}>
                  {renderedTitleHtml ? (
                    <TextV2
                      // The LCP element, large block-level text. The rich title HTML is sanitized
                      // at both save paths (console + inline dialog) to font-family/size spans only.
                      as="h1"
                      typographyVariant={eventNameTitleVariant}
                      textAlign={headerTextData?.textAlignment ?? 'center'}
                      color={multilineTitleColor}
                      __css={{ '& p, & div': { margin: 0 } }}
                      dangerouslySetInnerHTML={{ __html: renderedTitleHtml }}
                      {...mastheadTitleProps}
                    />
                  ) : (
                    <TextV2
                      // The LCP element, large block-level text
                      as="h1"
                      typographyVariant={eventNameTitleVariant}
                      {...mastheadTitleProps}
                    >
                      {eventDisplayName}
                    </TextV2>
                  )}
                </GuestSiteTypographyOverride>
              </InlineEditor>
            </StyledTitle>
            {/* Header controls, top-right: the guest language picker stacked above the admin
                Edit Event button. Stacking keeps the pair narrow so it doesn't run into the
                event title — the picker renders for guests, Edit Event only for admins on desktop. */}
            <Flex
              position="absolute"
              top="19px"
              right="16px"
              flexDirection="column"
              alignItems="flex-end"
              gap={3}
            >
              <GuestLanguageSelector surface="header" />
              {isAdmin && !isPreviewing && navBarVariant === 'inline' && (
                <ButtonV2
                  onClick={handleEditEventClicked}
                  intent="primary"
                  size="sm"
                  typographyVariant="button1"
                  variant="outline"
                  as={Link}
                  to={`/${eventHandle}/edit/dashboard`}
                  {...customEditEventButtonProps}
                >
                  {editEventText}
                </ButtonV2>
              )}
            </Flex>
            <NavBar
              handleMenuClicked={handleMenuClicked}
              title={eventDisplayName}
              variant={navBarVariant}
              data-testid={'header-nav'}
              eventHandle={eventHandle}
              pages={pages}
              eventProps={eventProps}
            />
            {!isMobileOrTablet && children}
          </Box>
        </Flex>
      </StyledRoot>

      {/* children will typically be the header photo, which should be placed below the component title banner on mobile/tablet*/}
      {isMobileOrTablet && children}
    </>
  );
};

EventNameNav.displayName = 'Header';

export { EventNameNav };
